/**
 * Bundle 156b - admin testimonial approval queue.
 *
 * Surfaces every consented-empty row (permission_granted=true
 * but current_display_text empty) plus any row carrying an
 * ai_draft_status. Admin can:
 *   * Draft with AI (only if untouched)
 *   * Approve & Publish (with optional inline edits)
 *   * Reject the current draft (keeps history, re-drafting
 *     allowed)
 *
 * NEVER auto-publishes. AI drafts land in `pending_admin_review`
 * status until an admin approves them.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Wand2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';

const _BUCKETS = [
  { key: 'untouched', label: 'Untouched' },
  { key: 'pending_admin_review', label: 'Pending review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'insufficient_source', label: 'Insufficient source' },
];

const _statusBadge = (s) => {
  const map = {
    pending_admin_review: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    insufficient_source: 'bg-neutral-200 text-neutral-700',
  };
  return map[s] || 'bg-neutral-100 text-neutral-500';
};

const AdminTestimonialApprovalQueuePage = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async (bucket = filter) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/testimonials/approval-queue', {
        params: bucket ? { filter: bucket } : {},
      });
      setRows(data.rows || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error('Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(null); }, []);
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line

  return (
    <div className="container mx-auto px-4 md:px-8 py-8"
          data-testid="admin-testimonial-approval-queue">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-primary-900 mb-1">
          Testimonial approval queue
        </h1>
        <p className="text-sm text-neutral-600">
          Consented rows awaiting draft or review. Nothing publishes without your approval.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" role="tablist">
        {_BUCKETS.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={() => setFilter(filter === b.key ? null : b.key)}
            data-testid={`queue-filter-${b.key}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
              filter === b.key
                ? 'bg-primary-900 text-white border-primary-900'
                : 'bg-white text-primary-800 border-neutral-200 hover:border-primary-300'
            }`}
          >
            {b.label}
            <span className="ml-2 text-[11px] font-normal opacity-70">
              {counts[b.key] || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-700" />
        </div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-neutral-500">
          Nothing in this bucket.
        </CardContent></Card>
      ) : (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Event</th>
                <th className="text-left px-3 py-2">Reporter</th>
                <th className="text-left px-3 py-2">Attribution</th>
                <th className="text-left px-3 py-2">AI draft</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                  onClick={() => setSelected(r)}
                  data-testid={`queue-row-${r.id}`}
                >
                  <td className="px-3 py-2 text-primary-900">
                    {r.event?.title || r.event_id}
                  </td>
                  <td className="px-3 py-2">
                    {r.reporter_first_name_masked || r.reporter_first_name || '-'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[10px]">
                      {r.attribution || 'initials'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${_statusBadge(r.ai_draft_status)}`}
                    >
                      {r.ai_draft_status || 'untouched'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-neutral-500">
                    {r.updated_at?.slice(0, 10) || '-'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline">Open</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <SlideOver
          testimonial={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); load(filter); }}
        />
      )}
    </div>
  );
};

const SlideOver = ({ testimonial, onClose, onChanged }) => {
  const [ctx, setCtx] = useState(null);
  const [editText, setEditText] = useState(
    testimonial?.ai_draft?.text || testimonial?.current_display_text || '');
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/testimonials/${testimonial.id}/context`);
        setCtx(data);
        if (data?.testimonial?.ai_draft?.text) {
          setEditText(data.testimonial.ai_draft.text);
        }
      } catch (e) { /* toast handled elsewhere */ }
    })();
  }, [testimonial?.id]);

  const draftWithAI = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/testimonials/${testimonial.id}/draft-with-ai`);
      if (data.status === 'insufficient_source') {
        toast.info('Source material was too sparse. You may still author manually.');
      } else if (data?.ai_draft?.text) {
        setEditText(data.ai_draft.text);
        toast.success('Draft ready. Review + edit before approving.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                     || 'AI draft failed');
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/testimonials/${testimonial.id}/approve`,
        { text_final: editText });
      toast.success('Testimonial published.');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                     || 'Approve failed');
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please add a rejection reason.');
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/admin/testimonials/${testimonial.id}/reject-draft`,
        { reason: rejectReason });
      toast.success('Draft rejected. You may redraft.');
      onChanged?.();
    } catch (e) {
      toast.error('Reject failed');
    } finally { setBusy(false); }
  };

  const charCount = (editText || '').length;
  const lenOk = charCount >= 100 && charCount <= 220;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="approval-slide-over"
    >
      <div
        className="bg-white w-full max-w-4xl overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-neutral-200 px-5 py-3 flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={onClose}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="text-sm font-medium text-primary-900">
            {testimonial.event?.title || testimonial.event_id}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5 p-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
              Survey answers
            </div>
            <pre className="text-[11px] bg-neutral-50 border border-neutral-200 rounded p-3 whitespace-pre-wrap break-words max-h-[60vh] overflow-auto"
                    data-testid="approval-survey-answers">
              {ctx?.context?.answers
                ? JSON.stringify(ctx.context.answers, null, 2)
                : 'Loading...'}
            </pre>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                Draft
              </div>
              <Textarea
                rows={7}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder='"Write the final quote here, or click Draft with AI."'
                data-testid="approval-textarea"
              />
              <div className={`text-[11px] mt-1 ${lenOk ? 'text-neutral-500' : 'text-red-600'}`}>
                {charCount} chars (100 to 220 required)
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">
                Preview
              </div>
              <div className="bg-neutral-50 border border-neutral-200 rounded p-3 text-sm italic text-primary-900"
                    data-testid="approval-preview">
                {editText ? `"${editText}"` : <span className="text-neutral-400">Empty draft</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                onClick={draftWithAI}
                disabled={busy}
                variant="outline"
                data-testid="approval-draft-ai"
              >
                <Wand2 className="h-4 w-4 mr-1" /> Draft with AI
              </Button>
              <Button
                onClick={approve}
                disabled={busy || !lenOk}
                className="bg-primary-900 text-white hover:bg-primary-800"
                data-testid="approval-approve"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve &amp; publish
              </Button>
            </div>
            <div className="pt-3 border-t border-neutral-100">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                Reject draft
              </div>
              <Textarea
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason (required)"
                data-testid="approval-reject-reason"
              />
              <Button
                onClick={reject}
                disabled={busy}
                variant="outline"
                className="mt-2 text-red-700 border-red-200"
                data-testid="approval-reject"
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTestimonialApprovalQueuePage;
